import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button, Table, Spinner, ProgressBar } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const fmtNum = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0';
  return n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Helper konversi tanggal YYYY-MM-DD <-> DD/MM/YYYY
const toDdmmyyyy = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const parts = yyyyMmDd.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return yyyyMmDd;
};

const toYyyymmdd = (ddMmYyyy) => {
  if (!ddMmYyyy) return '';
  const parts = ddMmYyyy.split('/');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return ddMmYyyy;
};

const OpnameApotek = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();

  // Lokasi Apotek: 00001 (Farmasi Rawat Jalan), 00002 (Farmasi Rawat Inap)
  const [lokasi, setLokasi] = useState(() => {
    return routeLocation.state?.lokasi || localStorage.getItem('simrs_lokasi') || '00001';
  });

  const lokasiOptions = [
    { kode: '00001', nama: 'RS Mary Cileungsi | Farmasi Rawat Jalan' },
    { kode: '00002', nama: 'RS Mary Cileungsi | Farmasi Rawat Inap' },
  ];

  const currentLokasiNama = lokasiOptions.find(l => l.kode === lokasi)?.nama || `Apotek (${lokasi})`;

  const [username, setUsername] = useState('Administrator');
  const [time, setTime] = useState({ date: '', clock: '' });

  // Clock
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      setTime({
        date: `${dd}/${mm}/${yyyy}`,
        clock: `${hh}:${min}:${ss}`
      });
    };
    updateTime();
    const iv = setInterval(updateTime, 1000);
    return () => clearInterval(iv);
  }, []);

  // Username
  useEffect(() => {
    try {
      const u = localStorage.getItem('nama_user') || localStorage.getItem('username') || localStorage.getItem('user');
      if (u) {
        setUsername(u);
        setTxtPetugas2(u);
      }
    } catch (_) {}
  }, []);

  // ========================================================
  // FORM STATE (Persis sesuai Gambas form)
  // ========================================================
  // Row 1: Kode Obat (Kuning), Nama Obat, Harga Beli
  const [txtKdObat, setTxtKdObat] = useState('');
  const [txtNmObat, setTxtNmObat] = useState('');
  const [txtHarga, setTxtHarga] = useState('');

  // Row 2: Prinsipal / Pabrik, Tanggal ED
  const [txtPrinsipal, setTxtPrinsipal] = useState('');
  const [txtTglED, setTxtTglED] = useState('');

  // Row 3: Status Laporan Dinas (opt1: Non OKT, opt2: OKT)
  const [statusOkt, setStatusOkt] = useState('1'); // '1' = Non OKT, '2' = OKT

  // Row 4: Sub-panel Teal (Nomor Kartu, Tanggal SO, Jumlah, Satuan)
  const [txtNoKartu, setTxtNoKartu] = useState('');
  const [txtTglSO, setTxtTglSO] = useState(() => {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });
  const [txtQty, setTxtQty] = useState('');
  const [txtSatuan, setTxtSatuan] = useState('');

  // Row 5: Petugas & Saksi
  const [txtPetugas1, setTxtPetugas1] = useState(''); // Opnamer
  const [txtSaksi1, setTxtSaksi1] = useState('');     // Saksi 1
  const [txtPetugas2, setTxtPetugas2] = useState('Administrator'); // Inputer
  const [txtSaksi2, setTxtSaksi2] = useState('');     // Saksi 2

  // Progress Bar & Status Text
  const [progressVal, setProgressVal] = useState(0);
  const [statusMsg, setStatusMsg] = useState('Siap untuk input Stok Opname');

  // Input Refs for Gambas tab-like Enter navigation
  const refKdObat = useRef(null);
  const refNoKartu = useRef(null);
  const refHarga = useRef(null);
  const refTglED = useRef(null);
  const refHiddenTglED = useRef(null);
  const refTglSO = useRef(null);
  const refHiddenTglSO = useRef(null);
  const refQty = useRef(null);
  const refPetugas1 = useRef(null);
  const refSaksi1 = useRef(null);
  const refPetugas2 = useRef(null);
  const refSaksi2 = useRef(null);

  // ========================================================
  // TABLE / LIST STATE
  // ========================================================
  const [soList, setSoList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [grandTotalQty, setGrandTotalQty] = useState(0);
  const [grandTotalPrice, setGrandTotalPrice] = useState(0);
  const [page] = useState(1);
  const [limit] = useState(25);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [isLoadingList, setIsLoadingList] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ show: false, message: '', variant: 'success' }), 4000);
  };

  // ========================================================
  // MODALS
  // ========================================================
  // 1. Modal Lookup Obat
  const [showModalObat, setShowModalObat] = useState(false);
  const [lookupDrugList, setLookupDrugList] = useState([]);
  const [lookupQuery, setLookupQuery] = useState('');
  const [isLoadingLookup, setIsLoadingLookup] = useState(false);

  // 2. Modal Cetak
  const [showModalCetak, setShowModalCetak] = useState(false);
  const [tahunSO, setTahunSO] = useState(() => new Date().getFullYear().toString());

  // 3. Modal PDF Viewer
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('Laporan Stok Opname');

  // ========================================================
  // FETCH LIST DATA SO TERINPUT
  // ========================================================
  const fetchSoList = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const url = `${getApiUrl()}/opname_list?lokasi=${lokasi}&tgl_so=${encodeURIComponent(filterDate || txtTglSO)}&search=${encodeURIComponent(searchKeyword)}&page=${page}&limit=${limit}`;
      const res = await fetch(url);
      const j = await res.json();
      if (res.ok && j.status === 1) {
        setSoList(j.data || []);
        setTotalCount(j.total || 0);
        setGrandTotalQty(j.total_qty || 0);
        setGrandTotalPrice(j.grand_total || 0);
      } else {
        setSoList([]);
      }
    } catch (err) {
      console.error('Gagal fetch data SO:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, [lokasi, filterDate, txtTglSO, searchKeyword, page, limit]);

  useEffect(() => {
    fetchSoList();
  }, [fetchSoList]);

  // ========================================================
  // CEK OBAT (KDBR / Enter di kotak kuning)
  // ========================================================
  const handleCekObat = async (codeOverride) => {
    const code = (codeOverride !== undefined ? codeOverride : txtKdObat).trim();
    if (!code) {
      handleOpenLookupObat();
      return;
    }

    setStatusMsg(`Memeriksa obat ${code}...`);
    try {
      const res = await fetch(`${getApiUrl()}/opname_cek_obat?kd_obat=${encodeURIComponent(code)}`);
      const j = await res.json();

      if (res.ok && j.status === 1 && j.data) {
        const d = j.data;
        setTxtKdObat(d.kd_obat || code);
        setTxtNmObat(d.nm_obat || '');
        setTxtHarga(d.harga ? String(d.harga) : (d.hda ? String(d.hda) : '0'));
        setTxtSatuan(d.nmsatuan || d.satuan || '');
        setTxtPrinsipal(d.pabrik || '-');
        setStatusOkt(d.status === '2' ? '2' : '1');
        setStatusMsg(`Obat ditemukan: ${d.nm_obat}`);

        // Gambas alur: setelah obat ditemukan, fokus ke Harga atau No Kartu
        setTimeout(() => refHarga.current?.focus(), 150);
      } else {
        showToast(`Kode Obat "${code}" belum terdaftar. Menampilkan pencarian...`, 'warning');
        setLookupQuery(code);
        handleOpenLookupObat(code);
      }
    } catch (e) {
      showToast('Gagal memeriksa obat: ' + e.message, 'danger');
    }
  };

  // Buka Modal Lookup Obat
  const handleOpenLookupObat = async (keyword = '') => {
    setShowModalObat(true);
    setIsLoadingLookup(true);
    try {
      const res = await fetch(`${getApiUrl()}/opname_cari_obat?q=${encodeURIComponent(keyword)}`);
      const j = await res.json();
      if (res.ok && j.status === 1) {
        setLookupDrugList(j.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingLookup(false);
    }
  };

  const handleSelectDrugFromLookup = (d) => {
    setTxtKdObat(d.kd_obat || '');
    setTxtNmObat(d.nm_obat || '');
    setTxtHarga(d.harga ? String(d.harga) : (d.hda ? String(d.hda) : '0'));
    setTxtSatuan(d.nmsatuan || d.satuan || '');
    setTxtPrinsipal(d.pabrik || '-');
    setStatusOkt(d.status === '2' ? '2' : '1');
    setShowModalObat(false);
    setStatusMsg(`Obat dipilih: ${d.nm_obat}`);

    setTimeout(() => refHarga.current?.focus(), 150);
  };

  // ========================================================
  // CEK NOMOR KARTU (Cektrs)
  // ========================================================
  const handleCekKartu = async () => {
    const card = txtNoKartu.trim().toUpperCase();
    if (!card) return;

    try {
      const res = await fetch(`${getApiUrl()}/opname_cek_kartu?no_kartu=${encodeURIComponent(card)}&lokasi=${lokasi}`);
      const j = await res.json();

      if (res.ok && j.status === 1 && j.exists && j.data) {
        const d = j.data;
        showToast('Data kartu sudah pernah diinput, silakan EDIT QTY datanya.', 'info');
        setTxtKdObat(d.kd_obat || '');
        setTxtNmObat(d.nm_obat || '');
        setTxtSatuan(d.satuan || '');
        setTxtQty(d.qty ? String(d.qty) : '');
        setTxtHarga(d.hbeli ? String(d.hbeli) : '');
        if (d.tgl_so) setTxtTglSO(d.tgl_so);
        if (d.expired) setTxtTglED(d.expired);
        if (d.petugas1) setTxtPetugas1(d.petugas1);
        if (d.saksi1) setTxtSaksi1(d.saksi1);
        if (d.petugas2) setTxtPetugas2(d.petugas2);
        if (d.saksi2) setTxtSaksi2(d.saksi2);

        setStatusMsg(`Kartu ${card} dimuat untuk edit Qty`);
        setTimeout(() => refQty.current?.focus(), 150);
      } else {
        setStatusMsg(`Kartu ${card} siap diinput baru`);
        setTimeout(() => refQty.current?.focus(), 150);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ========================================================
  // BERSIHKAN FORM
  // ========================================================
  const handleBersih = () => {
    setTxtNoKartu('');
    setTxtKdObat('');
    setTxtNmObat('');
    setTxtHarga('');
    setTxtPrinsipal('');
    setTxtTglED('');
    setTxtQty('');
    setTxtSatuan('');
    setStatusOkt('1');
    setStatusMsg('Form telah dibersihkan');
    setTimeout(() => refKdObat.current?.focus(), 150);
  };

  // ========================================================
  // SIMPAN DATA (cmdsimpan_Click)
  // ========================================================
  const handleSimpan = async () => {
    if (!txtNoKartu.trim()) {
      showToast('Nomor Kartu Stock Opname tidak boleh kosong!', 'warning');
      refNoKartu.current?.focus();
      return;
    }
    if (!txtKdObat.trim()) {
      showToast('Kode Obat harus diisi!', 'warning');
      refKdObat.current?.focus();
      return;
    }
    if (!txtQty || isNaN(parseFloat(txtQty))) {
      showToast('Quantity Stock harus diisi dan tidak boleh kosong!', 'warning');
      refQty.current?.focus();
      return;
    }

    if (!window.confirm('Data Sudah Benar [Ya/Tidak] ?')) {
      refKdObat.current?.focus();
      return;
    }

    setProgressVal(50);
    setStatusMsg('Menyimpan data kartu...');

    try {
      const payload = {
        lokasi,
        no_kartu: txtNoKartu.trim().toUpperCase(),
        kd_obat: txtKdObat.trim(),
        nm_obat: txtNmObat.trim(),
        satuan: txtSatuan.trim(),
        qty: parseFloat(txtQty) || 0,
        hbeli: parseFloat(txtHarga) || 0,
        tgl_so: txtTglSO.trim(),
        expired: txtTglED.trim(),
        petugas1: txtPetugas1.trim(),
        saksi1: txtSaksi1.trim(),
        petugas2: txtPetugas2.trim(),
        saksi2: txtSaksi2.trim(),
        flag: ''
      };

      const res = await fetch(`${getApiUrl()}/opname_simpan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json();

      setProgressVal(100);
      if (res.ok && j.status === 1) {
        showToast(j.msg || 'Data Stok Opname berhasil disimpan!', 'success');
        setStatusMsg(`Sukses: Kartu ${j.no_kartu} tersimpan.`);
        handleBersih();
        fetchSoList();
      } else {
        showToast(j.msg || 'Gagal menyimpan data', 'danger');
        setStatusMsg('Gagal menyimpan data');
      }
    } catch (e) {
      showToast('Kesalahan koneksi saat simpan: ' + e.message, 'danger');
      setStatusMsg('Error saat simpan');
    } finally {
      setTimeout(() => setProgressVal(0), 1000);
    }
  };

  // ========================================================
  // HAPUS DATA (cmdPrint2_Click / DELETE)
  // ========================================================
  const handleHapusKartu = async (kartuToDelete) => {
    const card = (kartuToDelete || txtNoKartu).trim().toUpperCase();
    if (!card) {
      showToast('Pilih atau masukkan Nomor Kartu SO yang akan dihapus', 'warning');
      refNoKartu.current?.focus();
      return;
    }

    if (!window.confirm(`Yakin Nomor kartu SO : ${card} Akan dihapus ?`)) {
      return;
    }

    setProgressVal(40);
    try {
      const res = await fetch(`${getApiUrl()}/opname_hapus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lokasi, no_kartu: card })
      });
      const j = await res.json();
      setProgressVal(100);

      if (res.ok && j.status === 1) {
        showToast(j.msg || `Kartu SO ${card} dihapus`, 'info');
        if (card === txtNoKartu.trim().toUpperCase()) {
          handleBersih();
        }
        fetchSoList();
      } else {
        showToast(j.msg || 'Gagal menghapus kartu', 'danger');
      }
    } catch (e) {
      showToast('Gagal menghapus: ' + e.message, 'danger');
    } finally {
      setTimeout(() => setProgressVal(0), 1000);
    }
  };

  // ========================================================
  // CETAK PDF HANDLERS
  // ========================================================
  const handleCetakPDF = (type) => {
    setShowModalCetak(false);
    let url = '';
    let title = '';

    if (type === 'harian') {
      url = `${getApiUrl()}/opname_cetak_laporan?lokasi=${lokasi}&tgl_so=${encodeURIComponent(txtTglSO)}&user=${encodeURIComponent(username)}`;
      title = `Laporan Opname Persediaan (${txtTglSO})`;
    } else if (type === 'sebelum') {
      url = `${getApiUrl()}/opname_cetak_sebelum_so?lokasi=${lokasi}&user=${encodeURIComponent(username)}`;
      title = `Laporan Stok Sebelum SO (SAPOTEK)`;
    } else if (type === 'setelah') {
      url = `${getApiUrl()}/opname_cetak_setelah_so?lokasi=${lokasi}&user=${encodeURIComponent(username)}`;
      title = `Laporan Perbandingan Stok Setelah SO (BANDINGSO)`;
    } else if (type === 'tahun') {
      url = `${getApiUrl()}/opname_cetak_tahun?lokasi=${lokasi}&tahun=${encodeURIComponent(tahunSO)}&user=${encodeURIComponent(username)}`;
      title = `Laporan Stok Opname Apotek Tahun ${tahunSO}`;
    }

    setPdfUrl(url);
    setPdfTitle(title);
    setShowModalPdf(true);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>

      {/* TOAST ALERT */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          background: toast.variant === 'danger' ? '#b91c1c' : toast.variant === 'warning' ? '#d97706' : toast.variant === 'info' ? '#0284c7' : '#059669',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 8,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontWeight: 600,
          fontSize: '0.86rem'
        }}>
          <i className={`bi ${toast.variant === 'danger' ? 'bi-exclamation-octagon' : toast.variant === 'warning' ? 'bi-exclamation-triangle' : toast.variant === 'info' ? 'bi-info-circle' : 'bi-check-circle'}`} />
          {toast.message}
        </div>
      )}

      {/* TOPBAR / HEADER RS MARY CILEUNGSI HIJAU */}
      <header style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: '#fff',
        padding: '6px 16px',
        borderBottom: '2px solid #0284c7',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/logo.png"
            alt="Logo RS"
            style={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              background: '#fff',
              borderRadius: 6,
              padding: 2
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 800, fontSize: '0.98rem', letterSpacing: '0.4px', color: '#fff' }}>
                RS MARY CILEUNGSI HIJAU
              </span>
              <span style={{
                background: '#0284c7',
                color: '#fff',
                fontSize: '0.68rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 12
              }}>
                STOK OPNAME APOTEK
              </span>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.2 }}>
              Jl. Raya Narogong KM 21 Cileungsi Bogor &bull; Telp. (021) 8249-2222 Fax. (021) 8249-3902
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#cbd5e1' }}>
            <div>{time.date}</div>
            <div style={{ fontWeight: 700, color: '#38bdf8' }}>{time.clock} WIB</div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            padding: '4px 10px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '0.78rem'
          }}>
            <i className="bi bi-person-circle text-teal" />
            <span style={{ fontWeight: 600 }}>{username}</span>
          </div>
          <Button
            variant="outline-light"
            size="sm"
            style={{ fontSize: '0.74rem', padding: '3px 10px', fontWeight: 600 }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door me-1" /> Beranda
          </Button>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <div style={{ flex: 1, padding: '12px 16px', maxWidth: '1280px', margin: '0 auto', width: '100%' }}>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* GAMBAS STYLE FORM CARD (PERSIS SESUAI SCREENSHOT) */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div style={{
          background: '#d0d7de',
          border: '2px solid #57606a',
          borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          marginBottom: 16
        }}>

          {/* 1. Header Bar: STOK OPNAME APOTEK */}
          <div style={{
            background: '#003366',
            color: '#ffffff',
            textAlign: 'center',
            padding: '8px 12px',
            fontWeight: 800,
            fontSize: '1rem',
            letterSpacing: '1px',
            borderBottom: '2px solid #002244'
          }}>
            STOK OPNAME APOTEK
          </div>

          {/* Form Inner Body */}
          <div style={{ padding: '12px 16px' }}>

            {/* BARIS 0: LOKASI APOTEK */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8
            }}>
              <div style={{
                background: '#004080',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.80rem',
                padding: '6px 14px',
                borderRadius: 3,
                width: '180px',
                textAlign: 'center',
                letterSpacing: '0.5px'
              }}>
                LOKASI APOTEK
              </div>
              <input
                type="text"
                value={lokasi}
                readOnly
                style={{
                  width: '90px',
                  fontWeight: 800,
                  fontSize: '0.88rem',
                  color: '#0033aa',
                  textAlign: 'center',
                  background: '#fff',
                  border: '1px solid #8c959f',
                  padding: '5px 8px',
                  borderRadius: 3
                }}
              />
              <select
                value={lokasi}
                onChange={(e) => setLokasi(e.target.value)}
                style={{
                  flex: 1,
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  color: '#0033aa',
                  background: '#fff',
                  border: '1px solid #8c959f',
                  padding: '5px 10px',
                  borderRadius: 3,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                {lokasiOptions.map(opt => (
                  <option key={opt.kode} value={opt.kode}>{opt.nama}</option>
                ))}
              </select>
            </div>

            {/* BARIS 1: KODE | NAMA BARANG | HARGA */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }}>
              <div style={{
                background: '#004080',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.78rem',
                padding: '6px 10px',
                borderRadius: 3,
                width: '278px',
                letterSpacing: '0.4px'
              }}>
                KODE | NAMA BARANG | HARGA
              </div>

              {/* Kotak Kuning Kode Obat */}
              <div style={{ display: 'flex', gap: 4, width: '130px' }}>
                <input
                  ref={refKdObat}
                  type="text"
                  placeholder="Kode Obat"
                  value={txtKdObat}
                  onChange={(e) => setTxtKdObat(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCekObat();
                    }
                  }}
                  style={{
                    width: '100%',
                    background: '#ffff55',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    border: '1px solid #666',
                    padding: '5px 8px',
                    borderRadius: 3,
                    outline: 'none'
                  }}
                  title="Tekan Enter untuk cek obat atau buka pencarian"
                />
              </div>

              {/* Nama Barang */}
              <div style={{ flex: 1, display: 'flex', gap: 4 }}>
                <input
                  type="text"
                  readOnly
                  placeholder="Nama Barang / Obat (Klik tombol cari di kanan jika belum terisi)"
                  value={txtNmObat}
                  onClick={() => handleOpenLookupObat(txtNmObat)}
                  style={{
                    flex: 1,
                    background: '#fff',
                    color: '#1e293b',
                    fontWeight: 700,
                    fontSize: '0.86rem',
                    border: '1px solid #8c959f',
                    padding: '5px 10px',
                    borderRadius: 3,
                    cursor: 'pointer'
                  }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  style={{ padding: '0 10px', background: '#0055aa', borderColor: '#004488' }}
                  onClick={() => handleOpenLookupObat(txtNmObat)}
                  title="Cari Obat"
                >
                  <i className="bi bi-search" />
                </Button>
              </div>

              {/* Harga */}
              <div style={{ width: '150px' }}>
                <input
                  ref={refHarga}
                  type="number"
                  placeholder="Harga"
                  value={txtHarga}
                  onChange={(e) => setTxtHarga(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      refTglED.current?.focus();
                    }
                  }}
                  style={{
                    width: '100%',
                    background: '#fff',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '0.86rem',
                    border: '1px solid #8c959f',
                    padding: '5px 8px',
                    borderRadius: 3,
                    textAlign: 'right'
                  }}
                />
              </div>
            </div>

            {/* BARIS 2: PRINSIPAL | TGL ED */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }}>
              <div style={{
                background: '#004080',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.78rem',
                padding: '6px 10px',
                borderRadius: 3,
                width: '278px',
                letterSpacing: '0.4px'
              }}>
                PRINSIPAL | TGL ED
              </div>

              {/* Input Prinsipal */}
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Nama Pabrik / Prinsipal"
                  value={txtPrinsipal}
                  onChange={(e) => setTxtPrinsipal(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#fff',
                    color: '#1e293b',
                    fontSize: '0.84rem',
                    border: '1px solid #8c959f',
                    padding: '5px 10px',
                    borderRadius: 3
                  }}
                />
              </div>

              {/* Input Tanggal Expired dengan Calendar Picker */}
              <div style={{
                width: '160px',
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
                border: '1px solid #8c959f',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative'
              }}>
                <input
                  ref={refTglED}
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={txtTglED}
                  onChange={(e) => setTxtTglED(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      refNoKartu.current?.focus();
                    }
                  }}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    padding: '5px 6px',
                    textAlign: 'center'
                  }}
                />
                <input
                  ref={refHiddenTglED}
                  type="date"
                  value={toYyyymmdd(txtTglED)}
                  onChange={(e) => {
                    if (e.target.value) {
                      setTxtTglED(toDdmmyyyy(e.target.value));
                      setTimeout(() => refNoKartu.current?.focus(), 150);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '32px',
                    right: 0,
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: 2
                  }}
                  title="Pilih Tanggal Expired dari Kalender"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => {
                    try {
                      refHiddenTglED.current?.showPicker();
                    } catch (_) {
                      refHiddenTglED.current?.focus();
                    }
                  }}
                  style={{
                    background: '#e0f2fe',
                    border: 'none',
                    borderLeft: '1px solid #cbd5e1',
                    color: '#0284c7',
                    padding: '5px 8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Pilih Tanggal dari Kalender"
                >
                  <i className="bi bi-calendar3" style={{ fontSize: '0.82rem' }} />
                </button>
              </div>
            </div>

            {/* BARIS 3: STATUS LAPORAN DINAS (OKT) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10
            }}>
              <div style={{
                background: '#004080',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.78rem',
                padding: '6px 10px',
                borderRadius: 3,
                width: '278px',
                letterSpacing: '0.4px'
              }}>
                STATUS LAPORAN DINAS
              </div>

              <div style={{
                flex: 1,
                background: '#e9ecef',
                border: '1px solid #adb5bd',
                borderRadius: 3,
                padding: '5px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 28,
                fontSize: '0.82rem',
                fontWeight: 700
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                  <input
                    type="radio"
                    name="statusOkt"
                    value="1"
                    checked={statusOkt === '1'}
                    onChange={() => setStatusOkt('1')}
                  />
                  <span>NON OBAT KERAS TERBATAS (OKT)</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0, color: statusOkt === '2' ? '#b91c1c' : '#000' }}>
                  <input
                    type="radio"
                    name="statusOkt"
                    value="2"
                    checked={statusOkt === '2'}
                    onChange={() => setStatusOkt('2')}
                  />
                  <span>OBAT KERAS TERBATAS (OKT)</span>
                </label>
              </div>
            </div>

            {/* BARIS 4: SUB-PANEL TEAL (NOMOR KARTU, TANGGAL, JUMLAH, SATUAN JUAL) */}
            <div style={{
              background: '#f1f5f9',
              border: '2px solid #006688',
              borderRadius: 4,
              overflow: 'hidden',
              marginBottom: 10
            }}>
              {/* Header Kolom Teal */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1fr 1.2fr',
                background: '#006688',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.80rem',
                textAlign: 'center',
                padding: '5px 0',
                letterSpacing: '0.5px'
              }}>
                <div>NOMOR KARTU</div>
                <div>TANGGAL</div>
                <div>JUMLAH</div>
                <div>SATUAN JUAL</div>
              </div>

              {/* Input Kolom Teal */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1fr 1.2fr',
                padding: '6px 8px',
                gap: 8,
                background: '#e2e8f0'
              }}>
                <div>
                  <input
                    ref={refNoKartu}
                    type="text"
                    placeholder="No. Kartu SO (misal: E001)"
                    value={txtNoKartu}
                    onChange={(e) => setTxtNoKartu(e.target.value.toUpperCase())}
                    onBlur={handleCekKartu}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCekKartu();
                      }
                    }}
                    style={{
                      width: '100%',
                      background: '#fff',
                      color: '#0f172a',
                      fontWeight: 800,
                      fontSize: '0.88rem',
                      border: '1px solid #64748b',
                      padding: '6px 10px',
                      borderRadius: 3,
                      textAlign: 'center'
                    }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#fff',
                  border: '1px solid #64748b',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <input
                    ref={refTglSO}
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={txtTglSO}
                    onChange={(e) => setTxtTglSO(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        refQty.current?.focus();
                      }
                    }}
                    style={{
                      flex: 1,
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: '#cc0000',
                      fontWeight: 800,
                      fontSize: '0.90rem',
                      padding: '5px 6px',
                      textAlign: 'center'
                    }}
                  />
                  <input
                    ref={refHiddenTglSO}
                    type="date"
                    value={toYyyymmdd(txtTglSO)}
                    onChange={(e) => {
                      if (e.target.value) {
                        setTxtTglSO(toDdmmyyyy(e.target.value));
                        setTimeout(() => refQty.current?.focus(), 150);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '32px',
                      right: 0,
                      height: '100%',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                    title="Pilih Tanggal SO dari Kalender"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => {
                      try {
                        refHiddenTglSO.current?.showPicker();
                      } catch (_) {
                        refHiddenTglSO.current?.focus();
                      }
                    }}
                    style={{
                      background: '#fee2e2',
                      border: 'none',
                      borderLeft: '1px solid #cbd5e1',
                      color: '#dc2626',
                      padding: '5px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Pilih Tanggal dari Kalender"
                  >
                    <i className="bi bi-calendar-event" style={{ fontSize: '0.82rem' }} />
                  </button>
                </div>

                <div>
                  <input
                    ref={refQty}
                    type="number"
                    placeholder="0"
                    value={txtQty}
                    onChange={(e) => setTxtQty(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        refPetugas1.current?.focus();
                      }
                    }}
                    style={{
                      width: '100%',
                      background: '#fff',
                      color: '#000',
                      fontWeight: 800,
                      fontSize: '0.90rem',
                      border: '1px solid #64748b',
                      padding: '6px 10px',
                      borderRadius: 3,
                      textAlign: 'right'
                    }}
                  />
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Satuan Jual"
                    value={txtSatuan}
                    onChange={(e) => setTxtSatuan(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#fff',
                      color: '#0f172a',
                      fontWeight: 700,
                      fontSize: '0.86rem',
                      border: '1px solid #64748b',
                      padding: '6px 10px',
                      borderRadius: 3,
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* BARIS 5: PETUGAS & SAKSI (2x2 Grid) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginBottom: 12
            }}>
              {/* Kolom Kiri: OPNAMER & INPUTER */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div>
                  <div style={{
                    background: '#004080',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textAlign: 'center',
                    padding: '4px 0',
                    borderRadius: '3px 3px 0 0'
                  }}>
                    OPNAMER
                  </div>
                  <input
                    ref={refPetugas1}
                    type="text"
                    placeholder="Nama Petugas Opname Fisik"
                    value={txtPetugas1}
                    onChange={(e) => setTxtPetugas1(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        refSaksi1.current?.focus();
                      }
                    }}
                    style={{
                      width: '100%',
                      background: '#fff',
                      border: '1px solid #8c959f',
                      borderTop: 'none',
                      padding: '6px 10px',
                      fontSize: '0.84rem',
                      fontWeight: 600,
                      borderRadius: '0 0 3px 3px'
                    }}
                  />
                </div>

                <div>
                  <div style={{
                    background: '#004080',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textAlign: 'center',
                    padding: '4px 0',
                    borderRadius: '3px 3px 0 0'
                  }}>
                    INPUTER
                  </div>
                  <input
                    ref={refPetugas2}
                    type="text"
                    placeholder="Nama Petugas Input Komputer"
                    value={txtPetugas2}
                    onChange={(e) => setTxtPetugas2(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        refSaksi2.current?.focus();
                      }
                    }}
                    style={{
                      width: '100%',
                      background: '#fff',
                      border: '1px solid #8c959f',
                      borderTop: 'none',
                      padding: '6px 10px',
                      fontSize: '0.84rem',
                      fontWeight: 600,
                      borderRadius: '0 0 3px 3px'
                    }}
                  />
                </div>
              </div>

              {/* Kolom Kanan: SAKSI 1 & SAKSI 2 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div>
                  <div style={{
                    background: '#004080',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textAlign: 'center',
                    padding: '4px 0',
                    borderRadius: '3px 3px 0 0'
                  }}>
                    SAKSI 1
                  </div>
                  <input
                    ref={refSaksi1}
                    type="text"
                    placeholder="Nama Saksi Opname 1"
                    value={txtSaksi1}
                    onChange={(e) => setTxtSaksi1(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        refPetugas2.current?.focus();
                      }
                    }}
                    style={{
                      width: '100%',
                      background: '#fff',
                      border: '1px solid #8c959f',
                      borderTop: 'none',
                      padding: '6px 10px',
                      fontSize: '0.84rem',
                      fontWeight: 600,
                      borderRadius: '0 0 3px 3px'
                    }}
                  />
                </div>

                <div>
                  <div style={{
                    background: '#004080',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textAlign: 'center',
                    padding: '4px 0',
                    borderRadius: '3px 3px 0 0'
                  }}>
                    SAKSI 2
                  </div>
                  <input
                    ref={refSaksi2}
                    type="text"
                    placeholder="Nama Saksi Opname 2"
                    value={txtSaksi2}
                    onChange={(e) => setTxtSaksi2(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSimpan();
                      }
                    }}
                    style={{
                      width: '100%',
                      background: '#fff',
                      border: '1px solid #8c959f',
                      borderTop: 'none',
                      padding: '6px 10px',
                      fontSize: '0.84rem',
                      fontWeight: 600,
                      borderRadius: '0 0 3px 3px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* BARIS 6: TOOLBAR AKSI BAWAH */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              background: '#bcc4cc',
              border: '1px solid #9aa4ae',
              borderRadius: 4,
              padding: '8px 12px',
              flexWrap: 'wrap'
            }}>
              {/* Progress status di kiri */}
              <div style={{ flex: 1, minWidth: '220px' }}>
                <div style={{ fontSize: '0.74rem', fontWeight: 600, color: '#334155', marginBottom: 2 }}>
                  {statusMsg}
                </div>
                <ProgressBar
                  now={progressVal}
                  animated={progressVal > 0 && progressVal < 100}
                  style={{ height: '8px', background: '#fff', border: '1px solid #94a3b8' }}
                />
              </div>

              {/* Tombol aksi sesuai icon dan urutan Gambas */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* PROSES SEMUA */}
                <button
                  onClick={() => {
                    fetchSoList();
                    showToast('Data diperbarui dari server', 'info');
                  }}
                  style={{
                    background: '#e2e8f0',
                    border: '1.5px solid #94a3b8',
                    borderRadius: 4,
                    padding: '6px 14px',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: '#0f172a',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Muat ulang dan sinkronkan data SO"
                >
                  <i className="bi bi-check-circle-fill text-success fs-6" />
                  <span>PROSES SEMUA</span>
                </button>

                {/* DELETE */}
                <button
                  onClick={() => handleHapusKartu()}
                  style={{
                    background: '#e2e8f0',
                    border: '1.5px solid #94a3b8',
                    borderRadius: 4,
                    padding: '6px 14px',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: '#0f172a',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Hapus data kartu SO yang aktif"
                >
                  <i className="bi bi-trash3-fill text-danger fs-6" />
                  <span>DELETE</span>
                </button>

                {/* CETAK */}
                <button
                  onClick={() => setShowModalCetak(true)}
                  style={{
                    background: '#e2e8f0',
                    border: '1.5px solid #94a3b8',
                    borderRadius: 4,
                    padding: '6px 14px',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: '#0f172a',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Cetak Laporan Opname"
                >
                  <i className="bi bi-printer-fill text-primary fs-6" />
                  <span>CETAK</span>
                </button>

                {/* SIMPAN */}
                <button
                  onClick={handleSimpan}
                  style={{
                    background: '#e2e8f0',
                    border: '1.5px solid #0055aa',
                    borderRadius: 4,
                    padding: '6px 16px',
                    fontWeight: 800,
                    fontSize: '0.80rem',
                    color: '#0055aa',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  title="Simpan data ke tabel SO"
                >
                  <i className="bi bi-floppy-fill text-primary fs-6" />
                  <span>SIMPAN</span>
                </button>

                {/* KELUAR */}
                <button
                  onClick={() => navigate('/home')}
                  style={{
                    background: '#e2e8f0',
                    border: '1.5px solid #94a3b8',
                    borderRadius: 4,
                    padding: '6px 14px',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: '#dc2626',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Keluar ke Dashboard"
                >
                  <i className="bi bi-x-circle-fill text-danger fs-6" />
                  <span>KELUAR</span>
                </button>
              </div>

            </div>

          </div>

        </div>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TABEL DATA HASIL INPUT KARTU SO */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #cbd5e1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}>
          {/* Header Tabel */}
          <div style={{
            padding: '10px 16px',
            background: '#0f172a',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-table text-teal" />
              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                Daftar Kartu Stok Opname Terinput ({currentLokasiNama})
              </span>
              <span style={{
                background: '#0284c7',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: '0.72rem',
                fontWeight: 700
              }}>
                {totalCount} Kartu
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Filter Tgl */}
              <input
                type="text"
                placeholder="Filter Tgl (DD/MM/YYYY)"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: '1px solid #475569',
                  background: '#1e293b',
                  color: '#fff',
                  fontSize: '0.76rem',
                  width: '150px'
                }}
              />

              {/* Filter Search */}
              <input
                type="text"
                placeholder="Cari Kartu / Obat..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  border: '1px solid #475569',
                  background: '#1e293b',
                  color: '#fff',
                  fontSize: '0.76rem',
                  width: '180px'
                }}
              />

              <Button
                variant="outline-info"
                size="sm"
                onClick={fetchSoList}
                style={{ fontSize: '0.76rem', padding: '3px 10px' }}
              >
                <i className="bi bi-arrow-clockwise me-1" /> Segarkan
              </Button>
            </div>
          </div>

          {/* Table Content */}
          <div style={{ overflowX: 'auto', maxHeight: '420px' }}>
            <Table hover bordered responsive style={{ margin: 0, fontSize: '0.78rem' }}>
              <thead style={{ background: '#f8fafc', color: '#1e293b', position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>No</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>No. Kartu</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Kode</th>
                  <th>Nama Obat / Alkes</th>
                  <th style={{ width: '70px', textAlign: 'center' }}>Satuan</th>
                  <th style={{ width: '90px', textAlign: 'right' }}>Harga Beli</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Qty Fisik</th>
                  <th style={{ width: '100px', textAlign: 'right' }}>Total (Rp)</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Expired</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Tgl SO</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Opnamer</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingList ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '30px' }}>
                      <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                      Memuat daftar data stok opname...
                    </td>
                  </tr>
                ) : soList.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      <i className="bi bi-inbox fs-3 d-block mb-1 opacity-50" />
                      Belum ada data stok opname untuk lokasi dan filter tanggal ini.
                    </td>
                  </tr>
                ) : (
                  soList.map((row, idx) => (
                    <tr
                      key={row.no_kartu}
                      onClick={() => {
                        setTxtNoKartu(row.no_kartu || '');
                        setTxtKdObat(row.kd_obat || '');
                        setTxtNmObat(row.nm_obat || '');
                        setTxtSatuan(row.satuan || '');
                        setTxtQty(row.qty ? String(row.qty) : '');
                        setTxtHarga(row.hbeli ? String(row.hbeli) : '');
                        if (row.tgl_so) setTxtTglSO(row.tgl_so);
                        if (row.expired) setTxtTglED(row.expired);
                        if (row.petugas1) setTxtPetugas1(row.petugas1);
                        if (row.saksi1) setTxtSaksi1(row.saksi1);
                        if (row.petugas2) setTxtPetugas2(row.petugas2);
                        if (row.saksi2) setTxtSaksi2(row.saksi2);
                        setStatusMsg(`Memuat kartu ${row.no_kartu} ke formulir`);
                      }}
                      style={{
                        cursor: 'pointer',
                        background: txtNoKartu === row.no_kartu ? '#e0f2fe' : 'transparent'
                      }}
                    >
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{(page - 1) * limit + idx + 1}</td>
                      <td style={{ textAlign: 'center', fontWeight: 800, color: '#0369a1' }}>{row.no_kartu}</td>
                      <td style={{ textAlign: 'center', fontFamily: 'monospace' }}>{row.kd_obat}</td>
                      <td style={{ fontWeight: 600 }}>{row.nm_obat}</td>
                      <td style={{ textAlign: 'center' }}>{row.satuan}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(row.hbeli)}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>{fmtNum(row.qty)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtNum(row.total)}</td>
                      <td style={{ textAlign: 'center' }}>{row.expired || '-'}</td>
                      <td style={{ textAlign: 'center' }}>{row.tgl_so || '-'}</td>
                      <td style={{ textAlign: 'center' }}>{row.petugas1 || '-'}</td>
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          style={{ padding: '0 6px', fontSize: '0.70rem' }}
                          onClick={() => handleHapusKartu(row.no_kartu)}
                          title="Hapus Kartu"
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Footer Total Summary */}
          <div style={{
            padding: '8px 16px',
            background: '#f1f5f9',
            borderTop: '1px solid #cbd5e1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div style={{ fontSize: '0.80rem', color: '#475569' }}>
              Menampilkan {soList.length} dari total <b>{totalCount}</b> baris kartu SO
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: '0.82rem' }}>
              <div>
                Total Fisik Qty: <strong style={{ color: '#059669' }}>{fmtNum(grandTotalQty)}</strong>
              </div>
              <div>
                Grand Total Nilai: <strong style={{ color: '#0284c7' }}>Rp {fmtNum(grandTotalPrice)}</strong>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL 1: LOOKUP CARI OBAT (TB001) */}
      {/* ══════════════════════════════════════════════════════════ */}
      <Modal show={showModalObat} onHide={() => setShowModalObat(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#003366', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.94rem', fontWeight: 700 }}>
            <i className="bi bi-capsule-pill me-2 text-warning" />
            Cari & Pilih Obat Apotek (TB001)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '14px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              type="text"
              autoFocus
              placeholder="Ketik Nama Obat atau Kode Barcode..."
              value={lookupQuery}
              onChange={(e) => {
                setLookupQuery(e.target.value);
                handleOpenLookupObat(e.target.value);
              }}
              style={{
                flex: 1,
                padding: '6px 12px',
                borderRadius: 4,
                border: '1.5px solid #0284c7',
                fontSize: '0.86rem',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <Table hover bordered size="sm" style={{ fontSize: '0.78rem' }}>
              <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: '80px', textAlign: 'center' }}>Kode</th>
                  <th>Nama Obat</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Satuan</th>
                  <th style={{ width: '100px', textAlign: 'right' }}>Harga Beli</th>
                  <th>Prinsipal / Pabrik</th>
                  <th style={{ width: '70px', textAlign: 'center' }}>Pilih</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingLookup ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Mencari data obat...
                    </td>
                  </tr>
                ) : lookupDrugList.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                      Tidak ada obat ditemukan dengan kata kunci "{lookupQuery}".
                    </td>
                  </tr>
                ) : (
                  lookupDrugList.map((d) => (
                    <tr
                      key={d.kd_obat}
                      onClick={() => handleSelectDrugFromLookup(d)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: 'monospace' }}>{d.kd_obat}</td>
                      <td style={{ fontWeight: 600 }}>{d.nm_obat}</td>
                      <td style={{ textAlign: 'center' }}>{d.nmsatuan || d.satuan}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(d.harga || d.hda)}</td>
                      <td>{d.pabrik || '-'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <Button variant="primary" size="sm" style={{ padding: '0 8px', fontSize: '0.70rem' }}>
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowModalObat(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL 2: PILIHAN CETAK LAPORAN SO */}
      {/* ══════════════════════════════════════════════════════════ */}
      <Modal show={showModalCetak} onHide={() => setShowModalCetak(false)} centered>
        <Modal.Header closeButton style={{ background: '#003366', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.94rem', fontWeight: 700 }}>
            <i className="bi bi-printer me-2 text-warning" />
            Pilih Jenis Laporan Stok Opname
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Opsi 1 */}
            <button
              onClick={() => handleCetakPDF('harian')}
              style={{
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1.5px solid #0284c7',
                borderRadius: 6,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e0f2fe'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
            >
              <div style={{ fontWeight: 700, color: '#0369a1', fontSize: '0.86rem' }}>
                <i className="bi bi-file-earmark-pdf me-2" />
                1. Laporan Opname Persediaan (Harian / Tgl SO)
              </div>
              <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                Mencetak daftar fisik obat yang telah diinput pada tanggal <b>{txtTglSO}</b>
              </div>
            </button>

            {/* Opsi 2 */}
            <button
              onClick={() => handleCetakPDF('sebelum')}
              style={{
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1.5px solid #0d9488',
                borderRadius: 6,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#ccfbf1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
            >
              <div style={{ fontWeight: 700, color: '#0f766e', fontSize: '0.86rem' }}>
                <i className="bi bi-box-seam me-2" />
                2. Laporan Stok Sebelum SO (Tabel SAPOTEK)
              </div>
              <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                Mencetak saldo master obat sebelum penyesuaian stok opname dilakukan
              </div>
            </button>

            {/* Opsi 3 */}
            <button
              onClick={() => handleCetakPDF('setelah')}
              style={{
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1.5px solid #8b5cf6',
                borderRadius: 6,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#ede9fe'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
            >
              <div style={{ fontWeight: 700, color: '#6d28d9', fontSize: '0.86rem' }}>
                <i className="bi bi-arrow-left-right me-2" />
                3. Laporan Perbandingan Stok Setelah SO (BANDINGSO)
              </div>
              <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                Mencetak selisih antara saldo komputer sebelum SO dan hasil perhitungan fisik
              </div>
            </button>

            {/* Opsi 4 */}
            <div style={{
              padding: '12px 14px',
              background: '#f8fafc',
              border: '1.5px solid #f59e0b',
              borderRadius: 6
            }}>
              <div style={{ fontWeight: 700, color: '#b45309', fontSize: '0.86rem', marginBottom: 4 }}>
                <i className="bi bi-calendar3 me-2" />
                4. Laporan Stok Opname Tahunan
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <input
                  type="number"
                  value={tahunSO}
                  onChange={(e) => setTahunSO(e.target.value)}
                  placeholder="Tahun (YYYY)"
                  style={{
                    width: '100px',
                    padding: '4px 8px',
                    borderRadius: 4,
                    border: '1px solid #cbd5e1',
                    fontWeight: 700,
                    fontSize: '0.84rem'
                  }}
                />
                <Button
                  variant="warning"
                  size="sm"
                  style={{ fontWeight: 700, fontSize: '0.78rem' }}
                  onClick={() => handleCetakPDF('tahun')}
                >
                  <i className="bi bi-printer me-1" /> Cetak Tahunan
                </Button>
              </div>
            </div>

          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowModalCetak(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* MODAL 3: IN-APP PDF VIEWER */}
      {/* ══════════════════════════════════════════════════════════ */}
      <Modal show={showModalPdf} onHide={() => setShowModalPdf(false)} size="xl" centered>
        <Modal.Header closeButton style={{ background: '#0f172a', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.94rem', fontWeight: 700 }}>
            <i className="bi bi-file-earmark-pdf-fill text-danger me-2" />
            {pdfTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh', background: '#334155' }}>
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title={pdfTitle}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Spinner animation="border" className="me-2" />
              Menyiapkan dokumen PDF...
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px', background: '#f8fafc' }}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => window.open(pdfUrl, '_blank')}
            style={{ fontWeight: 600, fontSize: '0.78rem' }}
          >
            <i className="bi bi-box-arrow-up-right me-1" /> Buka Tab Baru
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowModalPdf(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default OpnameApotek;
